import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { formatDate } from "../lib/format";
import { ArrowLeft, ClipboardList, Package, Award, CheckCircle2, Clock, Factory, Truck, XCircle } from "lucide-react";

const STATUS_META = {
  pending:       { label: "Pending",       color: "bg-yellow-50 text-yellow-700",   icon: Clock },
  in_production: { label: "In Production", color: "bg-purple-50 text-purple-700",   icon: Factory },
  dispatched:    { label: "Dispatched",    color: "bg-orange-50 text-orange-700",   icon: Truck },
  completed:     { label: "Completed",     color: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  cancelled:     { label: "Cancelled",     color: "bg-red-50 text-red-700",         icon: XCircle },
};

function StatMini({ icon: Icon, label, value }) {
  return (
    <div className="card-surface p-5">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="font-figtree text-sm text-ink-soft">{label}</div>
          <div className="font-outfit font-bold text-2xl text-ink mt-1 truncate">{value}</div>
        </div>
        <div className="h-10 w-10 rounded-xl bg-forest-soft text-forest grid place-items-center flex-shrink-0 ml-2">
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
      </div>
    </div>
  );
}

export default function PartyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/parties/${id}/detail`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-ink-soft font-figtree">Loading…</div>;
  if (!data) return <div className="text-ink-soft font-figtree">Not found</div>;

  const { party, orders, totals } = data;

  return (
    <div data-testid="party-detail-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-ink-soft hover:text-forest font-figtree font-medium mb-4 transition-colors duration-200">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <PageHeader
        title={party.name}
        subtitle={`Buyer · ${party.phone || "No phone"} · ${party.address || "No address"}`}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatMini icon={ClipboardList} label="Total Orders" value={totals.orders} />
        <StatMini icon={CheckCircle2} label="Completed" value={totals.completed} />
        <StatMini icon={Package} label="Units Ordered" value={totals.quantity} />
        <StatMini icon={Award} label="Top Brand" value={totals.top_brand || "—"} />
      </div>

      {party.notes && (
        <div className="card-surface p-5 mb-6">
          <div className="text-sm text-ink-soft font-figtree mb-1">Notes</div>
          <div className="font-figtree text-ink">{party.notes}</div>
        </div>
      )}

      <h2 className="font-outfit font-semibold text-xl text-ink mb-4">Order History</h2>
      <div className="card-surface overflow-hidden">
        {orders.length === 0 ? (
          <div className="p-8 text-center text-ink-soft font-figtree">No orders from this buyer yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sand/60">
                <tr>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Status</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Date</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Items</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Assigned</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const meta = STATUS_META[o.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  const items = o.items || [];
                  const totalUnits = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
                  const uniqueBrands = [...new Set(items.map((it) => it.brand))];
                  return (
                    <tr
                      key={o.id}
                      className="border-t border-line hover:bg-bone/60 cursor-pointer align-middle"
                      onClick={() => nav(`/orders/${o.id}`)}
                      data-testid={`party-order-row-${o.id}`}
                    >
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-figtree font-semibold ${meta.color}`}>
                          <Icon className="h-3.5 w-3.5" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-figtree text-ink-soft text-sm whitespace-nowrap">{formatDate(o.received_at)}</td>
                      <td className="px-6 py-4 font-figtree text-ink">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-outfit font-semibold text-ink">
                            {items.length} item{items.length !== 1 ? "s" : ""}
                          </span>
                          <span className="text-xs text-ink-soft">·</span>
                          <span className="text-sm text-ink-soft">{totalUnits} units</span>
                        </div>
                        {uniqueBrands.length > 0 && (
                          <div className="text-xs text-ink-mute mt-0.5 truncate max-w-[280px]">
                            {uniqueBrands.slice(0, 3).join(", ")}
                            {uniqueBrands.length > 3 && ` +${uniqueBrands.length - 3}`}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 font-figtree text-ink-soft text-sm">
                        {o.employee_id ? (
                          <Link to={`/employees/${o.employee_id}`} onClick={(e) => e.stopPropagation()} className="text-forest hover:underline">
                            {o.employee_name}
                          </Link>
                        ) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
